// =====================================================
// AUDIT REPORT TYPES - PDF Export
// Institution-grade compliance reporting
// =====================================================

import type { JournalStats } from './journal';
import type { CounterfactualStats } from './counterfactual';
import type { TDIScore } from './disciplineScore';

// =====================================================
// AUDIT REPORT PARAMS
// =====================================================

export interface AuditReportParams {
  dateRange: {
    from: Date;
    to: Date;
  };
  filters?: {
    marketType?: string;
    assetSymbol?: string;
  };
}

export type DateRangePreset = '7' | '30' | '90' | 'all' | 'custom';

// =====================================================
// AUDIT REPORT DATA STRUCTURES
// =====================================================

export interface AuditSummary {
  userName: string;
  userId: string;
  generatedAt: Date;
  dateRange: {
    from: Date;
    to: Date;
    label: string;
  };
  
  // TDI Score
  tdiScore: number | null;
  tdiTier: string;
  
  // Key KPIs
  decisionQualityScore: number;
  capitalPreservationRate: number;
  riskAvoidanceScore: number;
  
  // Decision counts
  totalAnalyses: number;
  tradeSignals: number;
  noTradeDecisions: number;
  waitDecisions: number;
  
  // Trade outcomes
  wins: number;
  losses: number;
  breakevens: number;
  winRate: number | null;
}

export interface AuditDisciplineMetrics {
  // TDI Components
  capitalPreservationScore: number;
  decisionConsistencyScore: number;
  riskAvoidanceScore: number;
  behavioralStabilityScore: number;
  
  // Status
  disciplineStatus: 'Stable' | 'Drift' | 'Overtrading';
  regimeCompliance: number;
  
  // Interpretation text
  interpretation: string;
}

export interface AuditCounterfactualEntry {
  asset: string;
  date: string;
  inputPrice: number;
  decision: string;
  reason: string | null;
  priceAfter1h: number | null;
  priceAfter4h: number | null;
  priceAfter24h: number | null;
  capitalPreservedR: number | null;
  capitalPreservedUsd: number | null;
}

export interface AuditCounterfactualSummary {
  totalNoTradeWaitDecisions: number;
  completedTracking: number;
  totalCapitalPreservedR: number;
  totalCapitalPreservedUsd: number;
  decisionsThatSavedCapital: number;
  decisionsThatMissedOpportunity: number;
  notableExamples: AuditCounterfactualEntry[];
}

export interface AuditOutcomeDistribution {
  wins: number;
  losses: number;
  breakevens: number;
  missed: number;
  cancelled: number;
  noTrade: number;
  wait: number;
  pending: number;
}

export interface AuditByCategory {
  byMarketType: Record<string, number>;
  byAsset: Record<string, number>;
  byRegime: Record<string, number>;
}

export interface AuditDecisionLogEntry {
  date: string;
  asset: string;
  decision: string;
  direction: string | null;
  regime: string | null;
  primaryReason: string | null;
  outcome: string;
  rMultiple: number | null;
}

// =====================================================
// COMPLETE AUDIT REPORT
// =====================================================

export interface AuditReport {
  summary: AuditSummary;
  disciplineMetrics: AuditDisciplineMetrics;
  counterfactual: AuditCounterfactualSummary;
  outcomeDistribution: AuditOutcomeDistribution;
  categoryBreakdown: AuditByCategory;
  decisionLog: AuditDecisionLogEntry[];
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================

export function formatDateRange(from: Date, to: Date): string {
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  };
  return `${from.toLocaleDateString('en-US', options)} - ${to.toLocaleDateString('en-US', options)}`;
}

export function getDateRangeFromPreset(preset: DateRangePreset): { from: Date; to: Date; label: string } {
  const to = new Date();
  let from = new Date();
  let label = '';
  
  switch (preset) {
    case '7':
      from.setDate(from.getDate() - 7);
      label = 'Last 7 Days';
      break;
    case '30':
      from.setDate(from.getDate() - 30);
      label = 'Last 30 Days';
      break;
    case '90':
      from.setDate(from.getDate() - 90);
      label = 'Last 90 Days';
      break;
    case 'all':
      from = new Date(2020, 0, 1); // Start from 2020
      label = 'All Time';
      break;
    default:
      from.setDate(from.getDate() - 30);
      label = 'Last 30 Days';
  }
  
  return { from, to, label };
}

export function getDisciplineInterpretation(metrics: AuditDisciplineMetrics): string {
  const parts: string[] = [];
  
  if (metrics.capitalPreservationScore >= 70) {
    parts.push('Capital preservation score is strong');
  } else if (metrics.capitalPreservationScore >= 50) {
    parts.push('Capital preservation score is moderate');
  } else {
    parts.push('Capital preservation needs improvement');
  }
  
  if (metrics.decisionConsistencyScore >= 70) {
    parts.push('decision consistency is excellent');
  } else if (metrics.decisionConsistencyScore >= 50) {
    parts.push('decision consistency is acceptable');
  } else {
    parts.push('decision consistency needs work');
  }
  
  if (metrics.riskAvoidanceScore >= 70) {
    parts.push('risk avoidance is well-managed');
  } else if (metrics.riskAvoidanceScore >= 50) {
    parts.push('risk avoidance is moderately good');
  } else {
    parts.push('risk management requires attention');
  }
  
  if (metrics.behavioralStabilityScore >= 70) {
    parts.push('behavior is stable');
  } else {
    parts.push('behavioral patterns show some variability');
  }
  
  return parts.join('; ') + '.';
}
